import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { DashboardWrapper, AIDialog, PlatformTitle } from 'src/shared';
import { ContentView, ResponsiveContentContainer } from '../drugs/styles';
import { useAppDispatch } from 'src/hooks';
import { getMedicalDeviceKDocuments, UserSelectors, adminForceReprocessMedicalDevice } from 'src/reducers/user';
import { getAuthToken } from 'src/utils/auth';
import { verboseLog, verboseWarn } from 'src/utils/config';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { ReactComponent as UltrasoundIcon } from 'src/assets/icons/ultrasound.svg';
import { ReactComponent as XrayIcon } from 'src/assets/icons/xray.svg';
import { ReactComponent as CTIcon } from 'src/assets/icons/ct.svg';
import { MedicalDeviceCategories, Category, SubCategory, CategoryStatistics } from 'src/config/medicalDeviceCategories';


// Types for usage statistics
interface UsageStats {
  annualImages: string;
  annualProcedures: string;
  costPerImage: string;
  lastUpdated: string;
}

interface ModalityUsageData {
  [key: string]: UsageStats;
}

// Styled components for the medical imaging gallery

const GalleryContainer = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 1200px) {
    padding: 30px 20px;
  }
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const MainContentContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  width: 100%;
  
  @media (max-width: 1400px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (min-width: 1600px) {
    width: 78%;
    margin-right: 20px;
  }
  
  @media (min-width: 1401px) and (max-width: 1599px) {
    width: 73%;
    margin-right: 20px;
  }
  
  @media (max-width: 1400px) {
    width: 100%;
    margin-right: 0;
  }
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: linear-gradient(135deg, #f0f9eb 0%, #e0f2fe 100%);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 1600px) {
    width: 22%;
  }
  
  @media (min-width: 1401px) and (max-width: 1599px) {
    width: 27%;
  }
  
  @media (max-width: 1400px) {
    width: 100%;
    min-height: 300px;
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 20px;
  overflow: hidden;
  touch-action: pan-y pinch-zoom;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 380px;
`;

const CarouselTrack = styled.div<{ currentIndex: number; itemsPerView: number }>`
  display: flex;
  transition: transform 0.5s ease-in-out;
  gap: 20px;
  justify-content: flex-start;
  align-items: center;
  min-height: 100%;
  width: 100%;
  transform: translateX(-${props => (props.currentIndex - 1) * (100 / props.itemsPerView)}%);
`;

const CarouselCard = styled.div<{ itemsPerView: number }>`
  flex: 0 0 calc(${props => 100 / props.itemsPerView}% - ${props => 20 * (props.itemsPerView - 1) / props.itemsPerView}px);
  background: white;
  border-radius: 20px;
  padding: 3vh 2.5vw;
  min-padding: 25px;
  max-padding: 45px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: none;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 25px;
    min-height: 300px;
  }
  
  @media (min-width: 1200px) {
    padding: 45px;
    min-height: 400px;
  }
`;

const ModalityIcon = styled.div`
  font-size: 4vw;
  margin-bottom: 2vh;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8vw;
  height: 8vw;
  min-width: 60px;
  min-height: 60px;
  max-width: 120px;
  max-height: 120px;
  
  svg {
    width: 100%;
    height: 100%;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
    width: 60px;
    height: 60px;
  }
  
  @media (min-width: 1200px) {
    font-size: 5rem;
    width: 120px;
    height: 120px;
  }
`;

const ModalityTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 15px;
  color: #1e293b;
  text-align: center;
`;

const ModalityDescription = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #64748b;
  margin: 0;
  text-align: center;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
`;

const NavButton = styled.button`
  background: #3b82f6;
  border: none;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2563eb;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
`;

const SwipeHint = styled.div`
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 15px;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const UsageStatsContainer = styled.div`
  margin-top: 15px;
  padding: 15px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  border: 1px solid #bae6fd;
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.85rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StatLabel = styled.span`
  color: #0369a1;
  font-weight: 500;
`;

const StatValue = styled.span`
  color: #0c4a6e;
  font-weight: 600;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #bae6fd;
  border-radius: 50%;
  border-top-color: #0369a1;
  animation: spin 1s ease-in-out infinite;
  
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.8rem;
  text-align: center;
  margin-top: 8px;
`;

// Add keyframes for spinner
const GlobalStyle = styled.div`
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Dot = styled.div<{ isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.isActive ? '#3b82f6' : '#cbd5e1'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.isActive ? '#2563eb' : '#94a3b8'};
  }
`;

const ModalityData: Array<{
  id: string;
  title: string;
  icon: string | React.ReactElement;
  description: string;
  path: string;
  searchQuery: string;
}> = [
  {
    id: 'xray',
    title: 'X-Ray',
    icon: <XrayIcon />,
    description: 'X-Ray imaging for bone and chest examinations',
    path: '/medical_imaging/xray',
    searchQuery: 'annual X-ray imaging statistics worldwide medical procedures 2024'
  },
  {
    id: 'ultrasonic',
    title: 'Ultrasonic',
    icon: <UltrasoundIcon />,
    description: 'Ultrasound imaging for real-time examination',
    path: '/medical_imaging/ultrasonic',
    searchQuery: 'annual ultrasound imaging statistics worldwide medical procedures 2024'
  },
  {
    id: 'ct',
    title: 'CT',
    icon: <CTIcon />,
    description: 'Computed Tomography for cross-sectional imaging',
    path: '/medical_imaging/ct',
    searchQuery: 'annual CT scan statistics worldwide medical procedures 2024'
  },
  {
    id: 'mri',
    title: 'MRI',
    icon: '🧠',
    description: 'Magnetic Resonance Imaging for detailed brain and body scans',
    path: '/medical_imaging/mri',
    searchQuery: 'annual MRI scan statistics worldwide medical procedures 2024'
  },
  {
    id: 'fundus',
    title: 'Fundus',
    icon: '🔍',
    description: 'Fundus photography for retinal examination',
    path: '/medical_imaging/fundus',
    searchQuery: 'annual fundus photography statistics worldwide ophthalmology 2024'
  },
  {
    id: 'oct',
    title: 'OCT',
    icon: '👁️',
    description: 'Optical Coherence Tomography for retinal imaging',
    path: '/medical_imaging/oct',
    searchQuery: 'annual OCT imaging statistics worldwide ophthalmology 2024'
  },
  {
    id: 'pet',
    title: 'PET',
    icon: '⚛️',
    description: 'Positron Emission Tomography for metabolic imaging',
    path: '/medical_imaging/pet',
    searchQuery: 'annual PET scan statistics worldwide medical procedures 2024'
  },
  {
    id: 'retinal_video',
    title: 'Retinal Video',
    icon: '🎥',
    description: 'Dynamic retinal imaging and analysis',
    path: '/medical_imaging/retinal_video',
    searchQuery: 'annual retinal video imaging statistics worldwide ophthalmology 2024'
  },
  {
    id: 'pupil_video',
    title: 'Pupil Video',
    icon: '👁️',
    description: 'Pupil dynamics and response analysis',
    path: '/medical_imaging/pupil_video',
    searchQuery: 'annual pupil video analysis statistics worldwide ophthalmology 2024'
  }
];

const MedicalImaging: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { profile } = UserSelectors();
  const isLoggedIn = Boolean(getAuthToken() && profile);
  const numDocsRender = 6; // how many medical device K documents to query and render from backend
  const [medicalDeviceDocs, setMedicalDeviceDocs] = useState<Array<any>>();
  const [isLoadingDocs, setIsLoadingDocs] = useState<boolean>(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [itemsPerView, setItemsPerView] = useState(3); // Dynamic calculation will update this
  const [pdfModalOpen, setPdfModalOpen] = useState<boolean>(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>('');
  const [currentPdfTitle, setCurrentPdfTitle] = useState<string>('');
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [isGuestUser, setIsGuestUser] = useState<boolean>(true);   // Assume guest by default
  const [isAdmin, setIsAdmin] = useState<boolean>(false);  // Admin status
  const [showAdminPanel, setShowAdminPanel] = useState<boolean>(false);  // Admin panel visibility
  const carouselRef = useRef<HTMLDivElement>(null);
  
  // Two-level navigation state
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [viewMode, setViewMode] = useState<'categories' | 'subcategories'>('categories');
  
  // Track current filter state
  const [currentFilter, setCurrentFilter] = useState<{category?: string; subCategory?: string} | null>(null);
  
  // Get user data from Redux store
  const { profile: userProfile } = UserSelectors();

  // Check if user is logged in and admin status
  useEffect(() => {
    // Check for authentication token
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    
    setIsGuestUser(!token && !userProfile);
    
    // Only check admin status if user is authenticated and has profile
    if (token && userProfile) {
      // Check if user has Django admin privileges
      const isDjangoAdmin = Boolean(userProfile.is_staff || userProfile.is_superuser);
      setIsAdmin(isDjangoAdmin);
    } else {
      // Guest users or users without profile are not admin
      setIsAdmin(false);
    }
  }, [userProfile]);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{top: number, left: number}>({top: 0, left: 0});
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Usage statistics state
  const [usageStats, setUsageStats] = useState<ModalityUsageData>({});
  const [loadingStats, setLoadingStats] = useState<{[key: string]: boolean}>({});
  const [statsErrors, setStatsErrors] = useState<{[key: string]: string}>({});

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Cache duration: 24 hours
  const CACHE_DURATION = 24 * 60 * 60 * 1000;

  // Calculate optimal number of items to display based on available space
  const calculateItemsPerView = useCallback(() => {
    const screenWidth = window.innerWidth;
    
    // Responsive breakpoints for items per view
    if (screenWidth >= 1600) {
      return 3; // Large screens: 3 items
    } else if (screenWidth >= 1200) {
      return 3; // Medium-large screens: 3 items
    } else if (screenWidth >= 900) {
      return 2; // Medium screens: 2 items
    } else if (screenWidth >= 600) {
      return 2; // Small-medium screens: 2 items
    } else {
      return 1; // Small screens: 1 item
    }
  }, []);

  // Update items per view when component mounts or window resizes
  useEffect(() => {
    const updateItemsPerView = () => {
      const newItemsPerView = calculateItemsPerView();
      verboseLog(`🔄 Carousel: Responsive items per view: ${newItemsPerView}`);
      setItemsPerView(newItemsPerView);
    };

    updateItemsPerView();
    
    // Add window resize listener
    const handleResize = () => {
      updateItemsPerView();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateItemsPerView]);

  // Ensure page scrolls to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    // Also try to reset focus to prevent any auto-focus issues
    if (document.body) {
      document.body.focus();
    }
  }, []);

  // Ensure page scrolls to top after loading completes
  useEffect(() => {
    if (!isLoadingDocs) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  }, [isLoadingDocs]);

  // Prevent scroll jumping during initial load
  useEffect(() => {
    const preventScroll = (e: Event) => {
      e.preventDefault();
      window.scrollTo(0, 0);
    };

    // Add scroll prevention for first 500ms
    document.addEventListener('scroll', preventScroll, { passive: false });
    
    setTimeout(() => {
      document.removeEventListener('scroll', preventScroll);
    }, 500);

    return () => {
      document.removeEventListener('scroll', preventScroll);
    };
  }, []);

  // Get cached usage statistics
  const getCachedStats = (modalityId: string): UsageStats | null => {
    try {
      const cached = localStorage.getItem(`usage_stats_${modalityId}`);
      if (cached) {
        const data = JSON.parse(cached);
        if (Date.now() - data.timestamp < CACHE_DURATION) {
          return data.stats;
        }
      }
    } catch (error) {
      console.warn('Failed to read cached stats:', error);
    }
    return null;
  };

  // Cache usage statistics
  const cacheStats = (modalityId: string, stats: UsageStats) => {
    try {
      localStorage.setItem(`usage_stats_${modalityId}`, JSON.stringify({
        stats,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Failed to cache stats:', error);
    }
  };

  // Mock GPT API call (replace with actual API endpoint)
  const fetchUsageStats = async (modalityId: string, searchQuery: string): Promise<UsageStats> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    
    // Mock data based on modality (replace with actual GPT API call)
    const mockData: { [key: string]: UsageStats } = {
      xray: {
        annualImages: '~2.5 billion',
        annualProcedures: '~1.8 billion',
        costPerImage: '$25-150',
        lastUpdated: new Date().toLocaleDateString()
      },
      ultrasonic: {
        annualImages: '~1.2 billion',
        annualProcedures: '~900 million',
        costPerImage: '$50-300',
        lastUpdated: new Date().toLocaleDateString()
      },
      ct: {
        annualImages: '~300 million',
        annualProcedures: '~250 million',
        costPerImage: '$200-800',
        lastUpdated: new Date().toLocaleDateString()
      },
      mri: {
        annualImages: '~80 million',
        annualProcedures: '~65 million',
        costPerImage: '$400-1,200',
        lastUpdated: new Date().toLocaleDateString()
      },
      fundus: {
        annualImages: '~45 million',
        annualProcedures: '~35 million',
        costPerImage: '$30-100',
        lastUpdated: new Date().toLocaleDateString()
      },
      oct: {
        annualImages: '~25 million',
        annualProcedures: '~20 million',
        costPerImage: '$80-200',
        lastUpdated: new Date().toLocaleDateString()
      },
      pet: {
        annualImages: '~8 million',
        annualProcedures: '~6 million',
        costPerImage: '$800-2,500',
        lastUpdated: new Date().toLocaleDateString()
      },
      retinal_video: {
        annualImages: '~3 million',
        annualProcedures: '~2.5 million',
        costPerImage: '$120-400',
        lastUpdated: new Date().toLocaleDateString()
      },
      pupil_video: {
        annualImages: '~1.5 million',
        annualProcedures: '~1.2 million',
        costPerImage: '$150-500',
        lastUpdated: new Date().toLocaleDateString()
      }
    };
    
    return mockData[modalityId] || {
      annualImages: 'Data unavailable',
      annualProcedures: 'Data unavailable',
      costPerImage: 'Data unavailable',
      lastUpdated: new Date().toLocaleDateString()
    };
  };

  // Load usage statistics for a modality
  const loadUsageStats = async (modalityId: string, searchQuery: string) => {
    // Check cache first
    const cached = getCachedStats(modalityId);
    if (cached) {
      setUsageStats(prev => ({ ...prev, [modalityId]: cached }));
      return;
    }

    // Set loading state
    setLoadingStats(prev => ({ ...prev, [modalityId]: true }));
    setStatsErrors(prev => ({ ...prev, [modalityId]: '' }));

    try {
      const stats = await fetchUsageStats(modalityId, searchQuery);
      setUsageStats(prev => ({ ...prev, [modalityId]: stats }));
      cacheStats(modalityId, stats);
    } catch (error) {
      console.error(`Failed to load stats for ${modalityId}:`, error);
      setStatsErrors(prev => ({ 
        ...prev, 
        [modalityId]: 'Failed to load statistics' 
      }));
    } finally {
      setLoadingStats(prev => ({ ...prev, [modalityId]: false }));
    }
  };

  // Fetch FDA Medical Device Clearance Documents from backend
  useEffect(() => {
    verboseLog('🚀 Starting to fetch FDA Medical Device Clearance Documents...');
    verboseLog('📊 numDocsRender:', numDocsRender);
    
    setIsLoadingDocs(true);
    setDocsError(null);
    
    dispatch(getMedicalDeviceKDocuments({ numDocs: numDocsRender })).then(
      (response) => {
        verboseLog('✅ API call successful, response:', response);
        let data_tuples = response?.payload?.data?.data_tuples;
        verboseLog('📋 data_tuples:', data_tuples);
        setMedicalDeviceDocs(data_tuples);
        setIsLoadingDocs(false);
        verboseLog('📊 FDA Medical Device Clearance Documents loaded:', data_tuples);
      }
    ).catch((error) => {
      console.error('❌ Failed to load medical device documents:', error);
      setDocsError(error.message || 'Failed to load documents');
      setIsLoadingDocs(false);
    });
  }, [dispatch]);

  // Load usage statistics for visible items (categories or sub-categories)
  useEffect(() => {
    if (viewMode === 'subcategories' && selectedCategory) {
      const startIndex = currentIndex - 1;
      const visibleSubCategories = selectedCategory.subCategories.slice(startIndex, startIndex + itemsPerView);
      visibleSubCategories.forEach(subCategory => {
        if (!usageStats[subCategory.code] && !loadingStats[subCategory.code]) {
          loadUsageStats(subCategory.code, subCategory.searchQuery);
        }
      });
    }
  }, [currentIndex, itemsPerView, usageStats, loadingStats, viewMode, selectedCategory]);

  // Helper function to resolve icon identifier to actual icon component
  const getIconComponent = (icon: string): string | React.ReactElement => {
    if (icon === 'xray') return <XrayIcon />;
    if (icon === 'ultrasound') return <UltrasoundIcon />;
    if (icon === 'ct') return <CTIcon />;
    // For emoji strings, return as-is
    return icon;
  };

  // Handle category click (show sub-categories)
  const handleCategoryClick = (category: Category) => {
    // For Surgical & Interventional Systems (category B), we don't have real sub-categories.
    // Navigate directly to its page with only category filter (no subCategory).
    if (category.code === 'B' && category.subCategories && category.subCategories.length > 0) {
      const sub = category.subCategories[0];
      if (sub.path) {
        const filterParams = new URLSearchParams({
          category: category.categoryNumber
        });
        navigate(`${sub.path}?${filterParams.toString()}`);
        return;
      }
    }

    // Default: show sub-categories view
    setSelectedCategory(category);
    setViewMode('subcategories');
    setCurrentIndex(1); // Reset carousel to first position
  };

  // Handle sub-category click (navigate to detail page with filter params)
  const handleSubCategoryClick = (subCategory: SubCategory, category: Category) => {
    verboseLog('🖱️ Sub-category card clicked!');
    verboseLog(`📋 Sub-category: ${subCategory.code} (${subCategory.title})`);
    verboseLog(`📋 Category: ${category.code} (${category.title})`);
    verboseLog(`🔍 Will navigate with category=${category.categoryNumber}, subCategory=${subCategory.code}`);
    
    // Navigate to detail page with filter parameters in URL
    if (subCategory.path) {
      const filterParams = new URLSearchParams({
        category: category.categoryNumber,
        subCategory: subCategory.code
      });
      navigate(`${subCategory.path}?${filterParams.toString()}`);
    }
  };

  // Handle back button (return to categories view)
  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setViewMode('categories');
    setCurrentIndex(1); // Reset carousel to first position
  };

  // Fetch filtered documents by category/sub-category
  const fetchFilteredDocuments = (categoryNumber: string, subCategoryCode: string) => {
    verboseLog('🚀 API CALL FIRED!');
    verboseLog(`📡 Requesting filtered documents:`);
    verboseLog(`   - Category: ${categoryNumber}`);
    verboseLog(`   - Sub-Category: ${subCategoryCode}`);
    verboseLog(`   - Number of docs: ${numDocsRender}`);
    
    setIsLoadingDocs(true);
    setDocsError(null);
    
    dispatch(getMedicalDeviceKDocuments({ 
      numDocs: numDocsRender,
      category: categoryNumber,
      subCategory: subCategoryCode
    })).then(
      (response) => {
        verboseLog('✅ API RESPONSE RECEIVED!');
        verboseLog(`📦 Full response:`, response);
        
        let data_tuples = response?.payload?.data?.data_tuples;
        verboseLog(`📋 Data tuples received:`, data_tuples);
        
        // Extract and log K numbers
        if (data_tuples && Array.isArray(data_tuples)) {
          const kNumbers = data_tuples.map((doc: any) => doc.k_number || doc.kNumber).filter(Boolean);
          verboseLog(`🔢 RETURNED K NUMBERS (${kNumbers.length} total):`);
          kNumbers.forEach((kNum: string, index: number) => {
            verboseLog(`   ${index + 1}. ${kNum}`);
          });
          
          // Log document details
          verboseLog(`📄 DOCUMENT DETAILS:`);
          data_tuples.forEach((doc: any, index: number) => {
            verboseLog(`   ${index + 1}. K${doc.k_number || doc.kNumber || 'N/A'}: ${doc.device_name || 'N/A'}`);
          });
        } else {
          verboseWarn('⚠️ No data_tuples found in response');
        }
        
        // Replace the current document list with filtered results
        setMedicalDeviceDocs(data_tuples);
        setCurrentFilter({ category: categoryNumber, subCategory: subCategoryCode });
        setIsLoadingDocs(false);
        
        verboseLog(`✅ Successfully replaced document list with ${data_tuples?.length || 0} filtered documents`);
        verboseLog('📊 Documents will now be displayed underneath the carousel viewer');
      }
    ).catch((error) => {
      console.error('❌ API CALL FAILED!');
      console.error(`   Category: ${categoryNumber}, Sub-Category: ${subCategoryCode}`);
      console.error(`   Error:`, error);
      setDocsError(error.message || 'Failed to load documents');
      setIsLoadingDocs(false);
    });
  };

  // Legacy handler for backward compatibility
  const handleModalityClick = (modalityId: string) => {
    const modality = ModalityData.find(m => m.id === modalityId);
    if (modality) {
      navigate(modality.path);
    }
  };

  const openPdfModal = (pdfUrl: string, title: string) => {
    setCurrentPdfUrl(pdfUrl);
    setCurrentPdfTitle(title);
    setPdfError(null); // Clear any previous errors
    setPdfModalOpen(true);
    
    // Check if the presigned URL has expired by examining the Expires parameter
    try {
      const url = new URL(pdfUrl);
      const expiresParam = url.searchParams.get('Expires');
      if (expiresParam) {
        const expiresTime = parseInt(expiresParam) * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        if (currentTime > expiresTime) {
          setPdfError('Document access expired or forbidden');
          return;
        }
      }
    } catch (error) {
      console.error('Error parsing PDF URL:', error);
      // Continue with normal loading if URL parsing fails
    }
    
    // Set up error detection timeout as fallback
    const errorTimeout = setTimeout(() => {
      // If no error is set after 15 seconds, assume the PDF is loading
      // This is a fallback for cases where the PDF fails to load silently
    }, 15000);
    
    // Store timeout ID for cleanup
    (window as any).pdfErrorTimeout = errorTimeout;
  };

  const closePdfModal = () => {
    setPdfModalOpen(false);
    setCurrentPdfUrl('');
    setCurrentPdfTitle('');
    setPdfError(null);
    
    // Clear any pending error timeout
    if ((window as any).pdfErrorTimeout) {
      clearTimeout((window as any).pdfErrorTimeout);
      (window as any).pdfErrorTimeout = null;
    }
  };

  // Handle closing modal on 'Esc' key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pdfModalOpen) {
        closePdfModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [pdfModalOpen]);

  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(1, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(totalPositions, prev + 1));
  };

  // Admin force re-process function
  const handleForceReprocess = async (kNumbers: string[]) => {
    try {
      const result = await dispatch(adminForceReprocessMedicalDevice({ kNumbers })).unwrap();
      alert(`✅ Force re-processing completed!\nProcessed: ${result.data.processed_count}\nErrors: ${result.data.errors.length}`);
      // Refresh the medical device data
      dispatch(getMedicalDeviceKDocuments({ numDocs: numDocsRender }));
    } catch (error: any) {
      console.error('Force re-process error:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to force re-process documents';
      alert(`❌ Error: ${errorMessage}`);
    }
  };

  const handleDotClick = (index: number) => {
    // Jump to the start of each page (positions 1, 4, 7)
    setCurrentIndex(index * itemsPerView + 1);
  };

  // Calculate total pages and positions
  // Calculate total pages and positions based on current view mode
  const currentItems = viewMode === 'categories' 
    ? MedicalDeviceCategories 
    : (selectedCategory?.subCategories || []);
  const totalPages = Math.ceil(currentItems.length / itemsPerView);
  const totalPositions = Math.max(1, currentItems.length - itemsPerView + 1); // At least 1 position

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, totalPositions]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimer) {
        clearTimeout(tooltipTimer);
      }
    };
  }, [tooltipTimer]);

  // Loading state
  if (isLoadingDocs) {
    return (
      <DashboardWrapper>
        <ContentView>
          {/* Gallery Header */}
          <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '400px',
              fontSize: '18px',
              color: '#666',
              background: 'white',
              borderRadius: '20px',
              padding: '40px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
            }}>
              {docsError ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⚠️</div>
                  <div style={{ fontSize: '1.2rem', marginBottom: '10px', color: '#dc2626' }}>
                    Error loading documents
                  </div>
                  <div style={{ fontSize: '1rem', color: '#6b7280' }}>
                    {docsError}
                  </div>
                  <button 
                    onClick={() => window.location.reload()} 
                    style={{
                      marginTop: '20px',
                      padding: '10px 20px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⏳</div>
                  <div style={{ fontSize: '1.2rem' }}>
                    Loading medical device documents...
                  </div>
                </div>
              )}
            </div>
          </ContentView>
      </DashboardWrapper>
    );
  }

  return (
    <>
      <GlobalStyle />
      <DashboardWrapper>
        <ContentView>
        <PlatformTitle
          title="Medical Imaging Platform"
          description="Explore multimodal medical imaging modalities and FDA medical device documents"
        />

        {/* Main Content Container - Responsive Layout */}
        <MainContentContainer>
          {/* Admin Panel */}
          {isAdmin && (
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                  🔧 Admin Panel
                </h3>
                <button
                  onClick={() => setShowAdminPanel(!showAdminPanel)}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  {showAdminPanel ? 'Hide' : 'Show'} Admin Tools
                </button>
              </div>
              
              {showAdminPanel && (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      const kNumbers = medicalDeviceDocs?.map(doc => doc.k_number).filter(Boolean) || [];
                      if (kNumbers.length > 0) {
                        if (window.confirm(`Force re-process ${kNumbers.length} documents with ChatGPT?\nK-numbers: ${kNumbers.join(', ')}`)) {
                          handleForceReprocess(kNumbers);
                        }
                      } else {
                        alert('No documents available to re-process');
                      }
                    }}
                    style={{
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    🔄 Re-process Current Documents
                  </button>
                  
                  <button
                    onClick={() => {
                      const kNumbers = prompt('Enter K-numbers to re-process (comma-separated):');
                      if (kNumbers) {
                        const kNumbersList = kNumbers.split(',').map(k => k.trim()).filter(Boolean);
                        if (kNumbersList.length > 0) {
                          if (window.confirm(`Force re-process ${kNumbersList.length} documents?\nK-numbers: ${kNumbersList.join(', ')}`)) {
                            handleForceReprocess(kNumbersList);
                          }
                        }
                      }
                    }}
                    style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    🎯 Re-process Specific K-numbers
                  </button>
                  
                  <div style={{ fontSize: '12px', color: '#64748b', alignSelf: 'center' }}>
                    ⚠️ This will call ChatGPT API and update the database
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Left Side - Current Content */}
          <LeftContent>
            {/* Carousel Section */}
            <div style={{
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              borderRadius: '16px',
              padding: '15px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Back button when viewing sub-categories */}
              {viewMode === 'subcategories' && selectedCategory && (
                <button
                  onClick={handleBackToCategories}
                  style={{
                    alignSelf: 'flex-start',
                    marginBottom: '10px',
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  ← Back to Categories
                </button>
              )}
              
              <CarouselContainer
                ref={carouselRef}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <CarouselTrack currentIndex={currentIndex} itemsPerView={itemsPerView}>
                  {viewMode === 'categories' ? (
                    // Show main categories (A-E)
                    MedicalDeviceCategories.map((category, index) => (
                      <CarouselCard
                        key={category.code}
                        itemsPerView={itemsPerView}
                        onClick={() => handleCategoryClick(category)}
                      >
                        <ModalityIcon>
                          {typeof category.icon === 'string' ? (
                            category.icon
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {getIconComponent(category.icon)}
                            </div>
                          )}
                        </ModalityIcon>
                        <ModalityTitle>{category.title}</ModalityTitle>
                        <ModalityDescription>{category.description}</ModalityDescription>
                      
                      {/* Category Statistics */}
                      <UsageStatsContainer>
                        {CategoryStatistics[category.code] ? (
                          <>
                            {CategoryStatistics[category.code].annualImages && (
                              <StatsRow>
                                <StatLabel>Annual Images:</StatLabel>
                                <StatValue>{CategoryStatistics[category.code].annualImages}</StatValue>
                              </StatsRow>
                            )}
                            <StatsRow>
                              <StatLabel>Annual Procedures:</StatLabel>
                              <StatValue>{CategoryStatistics[category.code].annualProcedures}</StatValue>
                            </StatsRow>
                            {CategoryStatistics[category.code].costPerImage && (
                              <StatsRow>
                                <StatLabel>Cost per Image:</StatLabel>
                                <StatValue>{CategoryStatistics[category.code].costPerImage}</StatValue>
                              </StatsRow>
                            )}
                            <StatsRow>
                              <StatLabel>Updated:</StatLabel>
                              <StatValue>{CategoryStatistics[category.code].lastUpdated}</StatValue>
                            </StatsRow>
                          </>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '10px', color: '#64748b', fontSize: '0.8rem' }}>
                            {category.subCategories.length} sub-categories
                          </div>
                        )}
                      </UsageStatsContainer>
                    </CarouselCard>
                  ))
                  ) : (
                    // Show sub-categories for selected category
                    selectedCategory?.subCategories.map((subCategory, index) => (
                      <CarouselCard
                        key={subCategory.code}
                        itemsPerView={itemsPerView}
                        onClick={() => handleSubCategoryClick(subCategory, selectedCategory)}
                      >
                        <ModalityIcon>
                          {typeof subCategory.icon === 'string' ? (
                            getIconComponent(subCategory.icon)
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {getIconComponent(subCategory.icon)}
                            </div>
                          )}
                        </ModalityIcon>
                        <ModalityTitle>{subCategory.title}</ModalityTitle>
                        <ModalityDescription>{subCategory.description}</ModalityDescription>
                      
                      {/* Sub-category Statistics */}
                      <UsageStatsContainer>
                        {CategoryStatistics[subCategory.code] ? (
                          <>
                            {CategoryStatistics[subCategory.code].annualImages && (
                              <StatsRow>
                                <StatLabel>Annual Images:</StatLabel>
                                <StatValue>{CategoryStatistics[subCategory.code].annualImages}</StatValue>
                              </StatsRow>
                            )}
                            <StatsRow>
                              <StatLabel>Annual Procedures:</StatLabel>
                              <StatValue>{CategoryStatistics[subCategory.code].annualProcedures}</StatValue>
                            </StatsRow>
                            {CategoryStatistics[subCategory.code].costPerImage && (
                              <StatsRow>
                                <StatLabel>Cost per Image:</StatLabel>
                                <StatValue>{CategoryStatistics[subCategory.code].costPerImage}</StatValue>
                              </StatsRow>
                            )}
                            <StatsRow>
                              <StatLabel>Updated:</StatLabel>
                              <StatValue>{CategoryStatistics[subCategory.code].lastUpdated}</StatValue>
                            </StatsRow>
                          </>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '10px', color: '#64748b', fontSize: '0.8rem' }}>
                            Click to view devices
                          </div>
                        )}
                      </UsageStatsContainer>
                    </CarouselCard>
                    ))
                  )}
                </CarouselTrack>
              </CarouselContainer>

              <NavigationButtons>
                <NavButton onClick={handlePrevious} disabled={currentIndex === 1}>
                  ← Previous
                </NavButton>
                <NavButton onClick={handleNext} disabled={currentIndex === totalPositions}>
                  Next →
                </NavButton>
              </NavigationButtons>

              <DotsContainer>
                {Array.from({ length: totalPages }, (_, index) => {
                  const pageStartPosition = index * itemsPerView + 1;
                  const isActive = currentIndex >= pageStartPosition && currentIndex < pageStartPosition + itemsPerView;
                  return (
                    <Dot
                      key={index}
                      isActive={isActive}
                      onClick={() => handleDotClick(index)}
                    />
                  );
                })}
              </DotsContainer>
              
              <SwipeHint>
                💡 Swipe left/right or use arrow keys to navigate • Click any card to explore
              </SwipeHint>
            </div>

            {/* FDA Medical Device Clearance Documents Gallery Section */}
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{
                fontSize: '1.8rem',
                fontWeight: '700',
                marginBottom: '20px',
                color: '#1e293b',
                textAlign: 'center'
              }}>
                📊 FDA Medical Device Clearance Documents
                {currentFilter && (
                  <span style={{
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    color: '#3b82f6',
                    marginLeft: '10px',
                    display: 'block',
                    marginTop: '5px'
                  }}>
                    {(() => {
                      const cat = MedicalDeviceCategories.find(c => c.categoryNumber === (currentFilter?.category || ''));
                      const sub = cat?.subCategories.find(s => s.code === currentFilter?.subCategory);
                      if (!cat) return '🔍 Filtered';
                      if (sub) {
                        // If the sub-category code equals category code (e.g., B), show only the category title
                        if (sub.code === cat.code) {
                          return `🔍 Filtered: ${cat.title}`;
                        }
                        return `🔍 Filtered: ${cat.title} • ${sub.title}`;
                      }
                      return `🔍 Filtered: ${cat.title}`;
                    })()}
                  </span>
                )}
              </h2>
              <p style={{
                fontSize: '1rem',
                color: '#64748b',
                textAlign: 'center',
                marginBottom: '30px'
              }}>
                {currentFilter 
                  ? (() => {
                      const cat = MedicalDeviceCategories.find(c => c.categoryNumber === (currentFilter?.category || ''));
                      const sub = cat?.subCategories.find(s => s.code === currentFilter?.subCategory);
                      if (!cat) return `Showing ${medicalDeviceDocs?.length || 0} documents`;
                      if (sub) {
                        if (sub.code === cat.code) {
                          return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title}`;
                        }
                        return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title} • ${sub.title}`;
                      }
                      return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title}`;
                    })()
                  : 'Latest FDA medical device clearance documents from our database'}
              </p>
              
              {/* Gallery Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '25px',
                marginBottom: '30px'
              }}>
                {medicalDeviceDocs?.slice(0, 6).map((doc: any, index: number) => (
                  <div key={index} style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    border: '2px solid #e2e8f0',
                    borderRadius: '16px',
                    padding: '25px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.15)';
                    e.currentTarget.style.borderColor = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                  >
                    
                    {/* Document Icon */}
                    <div style={{
                      fontSize: '3rem',
                      textAlign: 'center',
                      marginBottom: '15px',
                      color: '#3b82f6'
                    }}>
                      📋
                    </div>
                    
                    {/* K Number Badge - Clickable to Open PDF */}
                    <div 
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        textAlign: 'center',
                        marginBottom: '15px',
                        display: 'inline-block',
                        width: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#2563eb';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#3b82f6';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      onClick={() => {
                        if (doc.pdf_url) {
                          const deviceDisplayName = doc.company_name 
                            ? `${doc.device_name || 'Unknown Device'} from ${doc.company_name}`
                            : doc.device_name || 'Device name not available';
                          openPdfModal(doc.pdf_url, `${doc.k_number} - ${deviceDisplayName}`);
                        }
                      }}
                    >
                      📄 {doc.k_number || `Document ${index + 1}`}
                    </div>
                    
                    {/* Device Name from Company */}
                    <h3 style={{
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      marginBottom: '12px',
                      color: '#64748b',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      {doc.company_name 
                        ? `${doc.device_name || 'Unknown Device'} from ${doc.company_name}`
                        : doc.device_name || 'Device name not available'
                      }
                    </h3>
                    
                    {/* Tags Row */}
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      justifyContent: 'center',
                      marginBottom: '12px'
                    }}>
                      {/* Device Class Tag */}
                      {doc.device_class && (
                        <span style={{
                          fontSize: '0.7rem',
                          color: '#3b82f6',
                          backgroundColor: '#eff6ff',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontWeight: '500',
                          border: '1px solid #dbeafe'
                        }}>
                          {doc.device_class}
                        </span>
                      )}
                      
                      {/* Regulatory Pathway Tag */}
                      <span style={{
                        fontSize: '0.7rem',
                        color: '#059669',
                        backgroundColor: '#ecfdf5',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontWeight: '500',
                        border: '1px solid #bbf7d0'
                      }}>
                        {doc.document_type === '510(k) Premarket Notification' ? '510(k)' : 
                         doc.document_type === 'PMA' ? 'PMA' : 
                         doc.document_type === 'De Novo' ? 'De Novo' : '510(k)'}
                      </span>
                      
                      {/* SaMD Tag (if software) */}
                      {doc.is_samd && (
                        <span style={{
                          fontSize: '0.7rem',
                          color: '#dc2626',
                          backgroundColor: '#fef2f2',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontWeight: '500',
                          border: '1px solid #fecaca'
                        }}>
                          SaMD
                        </span>
                      )}
                      
                      {/* Additional Tag - Submission Status */}
                      <span style={{
                        fontSize: '0.7rem',
                        color: '#7c3aed',
                        backgroundColor: '#f3f4f6',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontWeight: '500',
                        border: '1px solid #e5e7eb'
                      }}>
                        Cleared
                      </span>
                    </div>
                    
                    {/* Intended Use */}
                    {doc.intended_use && (
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        textAlign: 'center',
                        marginBottom: '20px',
                        lineHeight: '1.3',
                        fontStyle: 'italic',
                        maxHeight: '40px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        <strong>Intended use:</strong> {doc.intended_use.length > 60 
                          ? `${doc.intended_use.substring(0, 60)}...`
                          : doc.intended_use
                        }
                      </p>
                    )}
                    
                    {/* Reason Around Device Button */}
                    <div style={{
                      marginTop: '20px',
                      position: 'relative'
                    }}>
                      <button
                        style={{
                          background: isLoggedIn ? '#10b981' : '#9ca3af',
                          color: 'white',
                          border: 'none',
                          borderRadius: '25px',
                          padding: '10px 20px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          cursor: isLoggedIn ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s ease',
                          width: '100%',
                          opacity: isLoggedIn ? 1 : 0.7
                        }}
                        onMouseEnter={(e) => {
                          if (isLoggedIn) {
                            e.currentTarget.style.background = '#059669';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          } else if (!isLoggedIn && !showTooltip) {
                            // Clear any existing timer
                            if (tooltipTimer) {
                              clearTimeout(tooltipTimer);
                            }
                            
                            // Calculate tooltip position
                            const rect = e.currentTarget.getBoundingClientRect();
                            const tooltipWidth = 200;
                            let left = rect.left + rect.width / 2;
                            
                            // Ensure tooltip doesn't go off-screen
                            if (left + tooltipWidth / 2 > window.innerWidth) {
                              left = window.innerWidth - tooltipWidth / 2 - 10;
                            } else if (left - tooltipWidth / 2 < 0) {
                              left = tooltipWidth / 2 + 10;
                            }
                            
                            setTooltipPosition({
                              top: rect.bottom + 20,
                              left: left
                            });
                            setShowTooltip(true);
                            
                            // Start 2-second timer to hide tooltip
                            const timer = setTimeout(() => {
                              setShowTooltip(false);
                              setTooltipTimer(null);
                            }, 2000);
                            setTooltipTimer(timer);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isLoggedIn) {
                            e.currentTarget.style.background = '#10b981';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                          // Tooltip will auto-hide after 2 seconds, no need to handle here
                        }}
                        onClick={() => {
                          if (isLoggedIn) {
                            // Navigate to reasoning page with K number
                            navigate(`/reasoning?k_number=${doc.k_number}`);
                          } else {
                            // If tooltip is visible, navigate to login
                            if (showTooltip) {
                              navigate('/login');
                            }
                          }
                        }}
                      >
                        🧠 Reason Around Device
                      </button>
                      
                      {/* Tooltip for unauthenticated users */}
                      {!isLoggedIn && showTooltip && (
                        <div 
                          style={{
                            position: 'fixed',
                            top: `${tooltipPosition.top}px`,
                            left: `${tooltipPosition.left}px`,
                            transform: 'translateX(-50%)',
                            backgroundColor: '#1e293b',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            lineHeight: '1.3',
                            maxWidth: '200px',
                            textAlign: 'center',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                            zIndex: 9999,
                            marginTop: '6px',
                            border: '1px solid #334155',
                            pointerEvents: 'none'
                          }}
                        >
                          <div style={{ 
                            marginBottom: '6px', 
                            fontSize: '0.7rem', 
                            opacity: 0.9
                          }}>
                            🔒 Sign up and subscribe to use this feature
                          </div>
                          <div style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}>
                            Sign In
                          </div>
                          
                          {/* Tooltip arrow */}
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '4px solid transparent',
                            borderRight: '4px solid transparent',
                            borderBottom: '4px solid #1e293b'
                          }} />
                        </div>
                      )}
                    </div>
                    
                    {/* Hover Effect Overlay */}
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      right: '0',
                      bottom: '0',
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
                      opacity: '0',
                      transition: 'opacity 0.3s ease',
                      pointerEvents: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0';
                    }} />
                  </div>
                ))}
              </div>
              
              {/* Gallery Info */}
              <div style={{
                textAlign: 'center',
                padding: '20px',
                background: '#f1f5f9',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#64748b',
                  margin: '0'
                }}>
                  💡 Click the blue K number badge to open FDA documents, or use the green "Reason Around Device" button for AI analysis
                  {!isLoggedIn && (
                    <span style={{ 
                      display: 'block', 
                      marginTop: '10px', 
                      color: '#ef4444',
                      fontWeight: '600'
                    }}>
                      🔒 Sign in required for AI reasoning features
                    </span>
                  )}
                </p>
              </div>
            </div>
          </LeftContent>

          {/* Right Side - AI Dialog */}
          <RightContent>
            <AIDialog
              isLoggedIn={isLoggedIn}
              title="AI Medical Imaging Assistant"
              subtitle="Ask me about medical imaging insights"
              placeholder="Ask about medical imaging insights..."
              width="100%"
              exampleConversations={[
                {
                  title: "Echocardiogram Analysis",
                  description: "Tutorial for LV volume analysis using Simpson's method",
                  turns: [
                    {
                      speaker: 'user',
                      message: "I need a model to analyze echocardiogram DICOM images to detect structural and functional information. Can you build this for me?",
                      delay: 0
                    },
                    {
                      speaker: 'ai',
                      message: "Absolutely! I'd be happy to help you build a comprehensive echocardiogram analysis system. To create the most effective solution for your needs, can you tell me what specific metrics you want to define and measure on the echocardiogram?",
                      delay: 1000
                    },
                    {
                      speaker: 'user',
                      message: "I want you to first locate the left ventricle area on the image, and start use Simpson's method, or method of disc to detect the long axis of LV area, and create perpendicular lines or discs, and measure the length and volume of LV area, then monitor this as a function of time and measure EDV, ESV and calculate EF from these metrics",
                      delay: 2000
                    },
                    {
                      speaker: 'ai',
                      message: "Perfect! I understand you want to implement Simpson's method for left ventricular volume analysis. Here's my comprehensive approach:\n\n**1. LV Detection & Segmentation**\n- Use deep learning (U-Net architecture) to automatically segment the left ventricle boundaries\n- Handle both 2D and 3D DICOM images\n- Ensure robust detection across different imaging planes\n\n**2. Simpson's Method Implementation**\n- Automatically identify the LV long axis\n- Create perpendicular discs along the long axis (typically 20 discs)\n- Calculate cross-sectional area for each disc using advanced contour detection\n- Apply Simpson's rule: Volume = Σ(Area_i × Disc_Thickness)\n\n**3. Temporal Analysis**\n- Track measurements across the entire cardiac cycle\n- Identify end-diastole (maximum volume) and end-systole (minimum volume)\n- Calculate EDV, ESV, and EF = (EDV - ESV) / EDV × 100%\n\n**4. Clinical Integration**\n- Generate clinical reports with measurements\n- Export results in DICOM-SR format for clinical workflow\n- Provide confidence scores and quality metrics\n\nI'll build this as a complete pipeline that processes your DICOM files and outputs the quantitative metrics you need. Should I start with a pilot study using some of your sample data?",
                      delay: 3000
                    }
                  ]
                },
                {
                  title: "Retinal Pulsation Analysis",
                  description: "Tutorial for automated retinal vessel pulsation measurement",
                  turns: [
                    {
                      speaker: 'user',
                      message: "I want to automatically extract pulsation metrics from retinal images and videos. Can you create this model for my practice?",
                      delay: 0
                    },
                    {
                      speaker: 'ai',
                      message: "Absolutely! Retinal pulsation analysis is a fascinating area with great clinical potential. To build the most effective solution, what specific pulsation metrics are you looking to measure, and do you have a particular region of interest in mind?",
                      delay: 1000
                    },
                    {
                      speaker: 'user',
                      message: "I want to measure vessel diameter changes over time, particularly in the optic nerve head region, and calculate pulsation amplitude and frequency. We're studying the relationship between retinal pulsation and intracranial pressure.",
                      delay: 2000
                    },
                    {
                      speaker: 'ai',
                      message: "Excellent! This is a cutting-edge application with significant clinical implications. Here's my comprehensive approach for retinal pulsation analysis:\n\n**1. Vessel Detection & Tracking**\n- Use advanced computer vision (YOLO + tracking algorithms) to identify retinal vessels\n- Focus specifically on vessels around the optic nerve head\n- Handle both fundus photography and OCT angiography data\n\n**2. Temporal Analysis**\n- Track vessel diameter changes frame-by-frame across video sequences\n- Apply advanced signal processing to filter noise and artifacts\n- Detect pulsation cycles using peak detection algorithms\n\n**3. Pulsation Metrics Calculation**\n- **Pulsation Amplitude**: (Max diameter - Min diameter) / Mean diameter × 100%\n- **Pulsation Frequency**: Beats per minute from temporal analysis\n- **Pulsation Index**: Amplitude normalized by vessel size\n- **Phase Relationships**: Synchronization between different vessels\n\n**4. ICP Correlation Analysis**\n- Statistical correlation with known ICP measurements\n- Machine learning models to predict ICP from pulsation patterns\n- Clinical validation against invasive ICP measurements\n\nThis system will be particularly valuable for non-invasive ICP monitoring. Should I start building this for your practice?",
                      delay: 3000
                    }
                  ]
                }
              ]}
            />
          </RightContent>
        </MainContentContainer>

      {/* PDF Viewer Modal */}
      {pdfModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fff',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => {
            // Close modal when clicking outside the content area
            if (e.target === e.currentTarget) {
              closePdfModal();
            }
          }}
        >
          {/* Modal Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              backgroundColor: '#1e293b',
              color: 'white',
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600' }}>
              {currentPdfTitle}
            </h3>
            <button
              onClick={closePdfModal}
              style={{
                backgroundColor: '#357edd',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#357edd';
              }}
            >
              ✕ Close
            </button>
          </div>

          {/* PDF Viewer */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {pdfError ? (
              // Error message display
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#f8fafc'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '40px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  maxWidth: '500px',
                  width: '100%'
                }}>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '20px'
                  }}>
                    {isGuestUser ? '⏰' : '❌'}
                  </div>
                  
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    marginBottom: '16px',
                    color: '#1e293b'
                  }}>
                    {isGuestUser ? 'Document Access Expired' : 'Document Access Error'}
                  </h2>
                  
                  <p style={{
                    fontSize: '16px',
                    color: '#64748b',
                    marginBottom: '24px',
                    lineHeight: '1.6'
                  }}>
                    {isGuestUser 
                      ? 'As a guest user, document access is limited to 1 hour for security reasons. The document link has expired.'
                      : 'There was an error accessing this document. Please try again or contact support if the issue persists.'
                    }
                  </p>
                  
                  {isGuestUser && (
                    <div style={{
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '24px'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#0369a1',
                        marginBottom: '12px'
                      }}>
                        🚀 Unlock Unlimited Access
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: '#0369a1',
                        marginBottom: '16px'
                      }}>
                        Sign up for a free account to access documents without time limitations!
                      </p>
                      <button
                        onClick={() => {
                          // Navigate to signup page or open signup modal
                          window.location.href = '/signup';
                        }}
                        style={{
                          backgroundColor: '#0ea5e9',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '12px 24px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#0284c7';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#0ea5e9';
                        }}
                      >
                        Sign Up for Free
                      </button>
                    </div>
                  )}
                  
                  <button
                    onClick={closePdfModal}
                    style={{
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#4b5563';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#6b7280';
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              // Normal PDF viewer with error boundary
              <div 
                style={{ height: '100%', width: '100%' }}
                onError={(e) => {
                  console.error('PDF container error:', e);
                  setPdfError('Failed to load PDF document');
                }}
              >
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                  <Viewer
                    fileUrl={currentPdfUrl}
                    onDocumentLoad={() => {
                      // Clear any previous errors when document loads successfully
                      setPdfError(null);
                      // Clear the error timeout since document loaded successfully
                      if ((window as any).pdfErrorTimeout) {
                        clearTimeout((window as any).pdfErrorTimeout);
                        (window as any).pdfErrorTimeout = null;
                      }
                    }}
                    renderPage={(props) => (
                      <div>
                        {props.canvasLayer.children}
                        {props.textLayer && props.textLayer.children}
                      </div>
                    )}
                  />
                </Worker>
              </div>
            )}
          </div>
        </div>
      )}

        </ContentView>
      </DashboardWrapper>
    </>
  );
};

export default MedicalImaging;
